<template>
  <div class="dashboard-container">
    <!-- Header Halaman -->
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
      <div>
        <h1 class="page-title mb-1">
          <i class="bi bi-calendar2-week-fill text-primary"></i> Jadwal Pertandingan Kontingen
        </h1>
        <p class="text-secondary small mb-0">
          Ringkasan partai, gelanggang, sudut atlet, lawan, serta alur partai selanjutnya.
        </p>
      </div>

      <div class="d-flex align-items-center gap-2">
        <button
          type="button"
          class="ui-btn ui-btn--primary"
          :disabled="memuat || mengunduhPdf || !contingentId || !matches.length"
          @click="unduhPdf"
          title="Unduh jadwal dalam format PDF padat dan rapat"
        >
          <span v-if="mengunduhPdf" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="bi bi-file-earmark-pdf-fill me-1 text-danger"></i>
          {{ mengunduhPdf ? 'Membuat PDF...' : 'Unduh Jadwal (PDF)' }}
        </button>
      </div>
    </div>

    <!-- Pemilih Kontingen & Turnamen -->
    <div class="ui-card mb-4">
      <div class="ui-card__body py-3">
        <div class="row g-3 align-items-center">
          <div class="col-md-6 col-lg-5">
            <label class="form-label small fw-bold text-muted mb-1">Pilih Kontingen:</label>
            <select
              v-model="contingentId"
              class="form-select form-select-sm"
              :disabled="memuatKontingen || contingents.length <= 1"
              @change="muatJadwal"
            >
              <option v-if="memuatKontingen" value="">Memuat daftar kontingen...</option>
              <option v-else-if="!contingents.length" value="">Tidak ada kontingen ditemukan</option>
              <option v-for="c in contingents" :key="c.id" :value="c.id">
                {{ c.name }} {{ c.pic_name ? `(PIC: ${c.pic_name})` : '' }}
              </option>
            </select>
          </div>

          <div class="col-md-6 col-lg-7" v-if="dataKontingen">
            <div class="d-flex flex-wrap align-items-center gap-3 bg-light p-2 rounded border small">
              <div>
                <span class="text-muted">Kontingen:</span>
                <strong class="ms-1 text-primary">{{ dataKontingen.name }}</strong>
              </div>
              <div v-if="dataTurnamen?.name">
                <span class="text-muted">Acara:</span>
                <strong class="ms-1">{{ dataTurnamen.name }}</strong>
              </div>
              <div v-if="dataKontingen.pic_phone">
                <span class="text-muted"><i class="bi bi-telephone"></i></span>
                <span class="ms-1">{{ dataKontingen.pic_phone }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ringkasan Statistik (KPI Bar) -->
    <div class="row g-2 mb-4" v-if="contingentId">
      <div class="col-6 col-md-3 col-xl-2">
        <div class="stat-pill border bg-white p-2.5 rounded shadow-sm">
          <div class="text-muted small fw-medium">Total Partai</div>
          <div class="fs-4 fw-bold text-dark">{{ stats.total_matches }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl-2">
        <div class="stat-pill border bg-white p-2.5 rounded shadow-sm">
          <div class="text-muted small fw-medium">Tanding</div>
          <div class="fs-4 fw-bold text-primary">{{ stats.tanding_count }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl-2">
        <div class="stat-pill border bg-white p-2.5 rounded shadow-sm">
          <div class="text-muted small fw-medium">Seni</div>
          <div class="fs-4 fw-bold text-purple">{{ stats.seni_count }}</div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl-2">
        <div class="stat-pill border bg-white p-2.5 rounded shadow-sm">
          <div class="text-muted small fw-medium">Sudut Biru</div>
          <div class="fs-4 fw-bold text-blue-custom">
            <i class="bi bi-square-fill me-1 fs-6"></i>{{ stats.blue_corner_count }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl-2">
        <div class="stat-pill border bg-white p-2.5 rounded shadow-sm">
          <div class="text-muted small fw-medium">Sudut Merah</div>
          <div class="fs-4 fw-bold text-danger">
            <i class="bi bi-square-fill me-1 fs-6"></i>{{ stats.red_corner_count }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl-2">
        <div class="stat-pill border bg-white p-2.5 rounded shadow-sm">
          <div class="text-muted small fw-medium">Selesai</div>
          <div class="fs-4 fw-bold text-success">{{ stats.completed_count }}</div>
        </div>
      </div>
    </div>

    <!-- Filter & Toolbar -->
    <div class="ui-card mb-4" v-if="contingentId">
      <div class="ui-card__body py-2.5">
        <div class="row g-2 align-items-center">
          <!-- Cari Nama / Partai -->
          <div class="col-12 col-xl-4 col-lg-3">
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-white border-end-0">
                <i class="bi bi-search text-muted"></i>
              </span>
              <input
                type="text"
                v-model="pencarian"
                class="form-control border-start-0"
                placeholder="Cari atlet, lawan, kelas, partai..."
              />
              <button
                v-if="pencarian"
                type="button"
                class="btn btn-outline-secondary"
                @click="pencarian = ''"
              >
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>

          <!-- Filter Gelanggang -->
          <div class="col-6 col-sm-3 col-xl-2">
            <select v-model="filterArena" class="form-select form-select-sm filter-select">
              <option value="">Semua Gelanggang</option>
              <option v-for="a in arenas" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>

          <!-- Filter Tanggal -->
          <div class="col-6 col-sm-3 col-xl-2">
            <select v-model="filterTanggal" class="form-select form-select-sm filter-select">
              <option value="">Semua Tanggal</option>
              <option v-for="d in dates" :key="d" :value="d">{{ formatTanggal(d) }}</option>
            </select>
          </div>

          <!-- Filter Kategori -->
          <div class="col-6 col-sm-3 col-xl-2">
            <select v-model="filterKategori" class="form-select form-select-sm filter-select">
              <option value="all">Semua Kategori</option>
              <option value="tanding">Tanding</option>
              <option value="seni">Seni</option>
            </select>
          </div>

          <!-- Filter Sudut -->
          <div class="col-6 col-sm-3 col-xl-2">
            <select v-model="filterSudut" class="form-select form-select-sm filter-select">
              <option value="">Semua Sudut</option>
              <option value="blue">Sudut Biru</option>
              <option value="red">Sudut Merah</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Kondisi Memuat -->
    <div v-if="memuat" class="ui-card p-5 text-center">
      <div class="spinner-border text-primary mb-2" role="status"></div>
      <p class="text-muted small mb-0">Memuat jadwal pertandingan kontingen...</p>
    </div>

    <!-- Kondisi Kosong -->
    <div v-else-if="!contingentId" class="ui-card p-5 text-center">
      <i class="bi bi-shield-exclamation fs-1 text-muted mb-2 d-block"></i>
      <h5 class="fw-bold text-dark">Pilih Kontingen</h5>
      <p class="text-muted small mb-0">Pilih kontingen di atas untuk melihat jadwal pertandingannya.</p>
    </div>

    <div v-else-if="!daftarTersaring.length" class="ui-card p-5 text-center">
      <i class="bi bi-calendar-x fs-1 text-muted mb-2 d-block"></i>
      <h5 class="fw-bold text-dark">Tidak Ada Partai Ditemukan</h5>
      <p class="text-muted small mb-3">
        {{ pencarian || filterArena || filterTanggal || filterSudut || filterKategori !== 'all'
          ? 'Tidak ada partai yang cocok dengan kriteria filter di atas.'
          : 'Belum ada jadwal partai yang diterbitkan oleh panitia untuk kontingen ini.' }}
      </p>
      <button
        v-if="pencarian || filterArena || filterTanggal || filterSudut || filterKategori !== 'all'"
        type="button"
        class="ui-btn ui-btn--secondary mx-auto"
        @click="resetFilter"
      >
        <i class="bi bi-arrow-counterclockwise me-1"></i> Reset Filter
      </button>
    </div>

    <!-- TABEL JADWAL PERTANDINGAN (DESKTOP) -->
    <div v-else class="ui-card overflow-hidden">
      <!-- Toolbar Hasil -->
      <div class="ui-card__header d-flex justify-content-between align-items-center py-2 px-3 border-bottom bg-light">
        <span class="small text-muted">
          Menampilkan <strong>{{ daftarTersaring.length }}</strong> dari <strong>{{ matches.length }}</strong> total partai
        </span>
        <span class="badge bg-secondary-subtle text-dark border">
          <i class="bi bi-clock me-1"></i> Terjadwal
        </span>
      </div>

      <!-- Desktop Table -->
      <div class="table-responsive d-none d-md-block">
        <table class="table table-hover align-middle mb-0 custom-schedule-table">
          <thead>
            <tr>
              <th style="width: 45px;" class="text-center">No</th>
              <th style="width: 100px;">Gelanggang</th>
              <th style="width: 75px;" class="text-center">Partai</th>
              <th style="width: 110px;">Hari / Waktu</th>
              <th style="width: 100px;" class="text-center">Sudut</th>
              <th style="min-width: 140px;">Atlet Kontingen</th>
              <th style="min-width: 130px;">Kategori & Kelas</th>
              <th style="min-width: 140px;">Lawan</th>
              <th style="min-width: 130px;">Partai Selanjutnya</th>
              <th style="width: 85px;" class="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(m, idx) in daftarTersaring" :key="m.id">
              <!-- No -->
              <td class="text-center text-muted fw-bold">{{ idx + 1 }}</td>

              <!-- Gelanggang -->
              <td>
                <div class="fw-bold text-dark">{{ m.arena_name }}</div>
                <div class="text-muted small">{{ m.pool_name }}</div>
              </td>

              <!-- Partai -->
              <td class="text-center">
                <span class="partai-num">{{ m.party_number }}</span>
                <div class="text-muted extra-small">{{ m.round_label }}</div>
              </td>

              <!-- Hari / Waktu -->
              <td>
                <div class="fw-medium text-dark small">
                  {{ m.scheduled_date ? formatTanggal(m.scheduled_date) : '-' }}
                </div>
                <div class="text-muted extra-small" v-if="m.start_time">
                  <i class="bi bi-clock me-1"></i>{{ m.start_time }} WIB
                </div>
              </td>

              <!-- Sudut -->
              <td class="text-center">
                <span
                  class="badge-corner"
                  :class="{
                    'badge-corner--blue': m.corner === 'blue',
                    'badge-corner--red': m.corner === 'red',
                    'badge-corner--neutral': m.corner !== 'blue' && m.corner !== 'red'
                  }"
                >
                  {{ m.corner === 'blue' ? 'SUDUT BIRU' : (m.corner === 'red' ? 'SUDUT MERAH' : 'SENI') }}
                </span>
              </td>

              <!-- Atlet Kontingen -->
              <td>
                <div class="fw-bold text-dark">{{ m.athlete_name }}</div>
                <div class="text-muted extra-small">{{ dataKontingen?.name }}</div>
              </td>

              <!-- Kategori & Kelas -->
              <td>
                <div class="d-flex align-items-center gap-1.5 mb-0.5">
                  <span
                    class="badge"
                    :class="m.match_type === 'tanding' ? 'bg-primary-subtle text-primary border border-primary' : 'bg-warning-subtle text-warning-emphasis border border-warning'"
                  >
                    {{ m.match_type_label }}
                  </span>
                  <span class="fw-semibold text-dark small">{{ m.class_name }}</span>
                </div>
                <div class="text-secondary extra-small">
                  {{ m.age_category_name }} &bull; {{ labelJK(m.gender) }}
                </div>
              </td>

              <!-- Lawan -->
              <td>
                <div class="fw-semibold text-dark">{{ m.opponent_name }}</div>
                <div class="text-muted extra-small" v-if="m.opponent_contingent && m.opponent_contingent !== '-'">
                  <i class="bi bi-shield me-0.5"></i> {{ m.opponent_contingent }}
                </div>
              </td>

              <!-- Partai Selanjutnya (Rute Kemenangan) -->
              <td>
                <div v-if="m.next_match?.is_final" class="next-pill next-pill--final">
                  <i class="bi bi-trophy-fill text-warning me-1"></i>
                  <strong>Partai Final</strong>
                </div>
                <div v-else-if="m.next_match?.label" class="next-pill next-pill--next">
                  <i class="bi bi-arrow-right-circle-fill text-success me-1"></i>
                  <span class="next-label">{{ m.next_match.label }}</span>
                </div>
                <div v-else class="text-muted extra-small">-</div>
              </td>

              <!-- Status -->
              <td class="text-center">
                <span v-if="m.status === 'completed'" class="badge" :class="m.is_winner ? 'bg-success' : 'bg-secondary'">
                  <i class="bi" :class="m.is_winner ? 'bi-check-circle-fill' : 'bi-x-circle'"></i>
                  {{ m.is_winner ? 'Menang' : 'Gugur' }}
                </span>
                <span v-else class="badge bg-light text-secondary border">
                  Menunggu
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card List (< 768px) -->
      <div class="d-md-none p-2 space-y-2">
        <div
          v-for="m in daftarTersaring"
          :key="m.id"
          class="card mb-2.5 border shadow-sm"
          :class="{
            'border-start-blue': m.corner === 'blue',
            'border-start-red': m.corner === 'red'
          }"
        >
          <div class="card-body p-3">
            <!-- Header Card: Gelanggang & Partai & Corner -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="d-flex align-items-center gap-1.5">
                <span class="badge bg-secondary-subtle text-secondary-emphasis">{{ m.arena_name }}</span>
                <span class="badge bg-light text-dark border fw-bold">Partai #{{ m.party_number }}</span>
              </div>
              <span
                class="badge-corner badge-corner--sm"
                :class="{
                  'badge-corner--blue': m.corner === 'blue',
                  'badge-corner--red': m.corner === 'red',
                  'badge-corner--neutral': m.corner !== 'blue' && m.corner !== 'red'
                }"
              >
                {{ m.corner === 'blue' ? 'BIRU' : (m.corner === 'red' ? 'MERAH' : 'SENI') }}
              </span>
            </div>

            <!-- Atlet vs Lawan -->
            <div class="matchup-block bg-light p-2 rounded mb-2">
              <div class="d-flex justify-content-between align-items-start mb-1">
                <div>
                  <small class="text-muted d-block" style="font-size: 10px;">ATLET KONTINGEN</small>
                  <strong class="text-dark">{{ m.athlete_name }}</strong>
                </div>
                <span v-if="m.status === 'completed'" class="badge" :class="m.is_winner ? 'bg-success' : 'bg-secondary'">
                  {{ m.is_winner ? 'Menang' : 'Gugur' }}
                </span>
              </div>
              <div class="border-top my-1"></div>
              <div>
                <small class="text-muted d-block" style="font-size: 10px;">LAWAN</small>
                <div class="fw-semibold text-secondary">{{ m.opponent_name }}</div>
                <small class="text-muted" v-if="m.opponent_contingent && m.opponent_contingent !== '-'">
                  {{ m.opponent_contingent }}
                </small>
              </div>
            </div>

            <!-- Kategori & Babak -->
            <div class="d-flex flex-wrap justify-content-between align-items-center small text-muted mb-2">
              <span>{{ m.class_full }}</span>
              <span class="badge bg-secondary-subtle text-dark">{{ m.round_label }}</span>
            </div>

            <!-- Partai Selanjutnya -->
            <div class="next-pill-mobile p-1.5 rounded bg-success-subtle border border-success-subtle text-success-emphasis small">
              <div class="d-flex align-items-center gap-1">
                <i class="bi bi-arrow-right-circle-fill text-success"></i>
                <strong style="font-size: 11px;">Rute Jika Menang:</strong>
              </div>
              <div class="ms-3 fw-bold" style="font-size: 11.5px;">
                {{ m.next_match?.label || (m.next_match?.is_final ? 'Partai Final' : '-') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { labelJK } from '@/utils/jk';
import http from "@/services/http";
import { useToast } from "vue-toastification";

export default {
  name: "ContingentSchedulePage",

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      contingents: [],
      contingentId: "",
      dataKontingen: null,
      dataTurnamen: null,
      matches: [],
      stats: {
        total_matches: 0,
        tanding_count: 0,
        seni_count: 0,
        blue_corner_count: 0,
        red_corner_count: 0,
        completed_count: 0,
        upcoming_count: 0,
      },
      arenas: [],
      dates: [],

      // Filter
      pencarian: "",
      filterArena: "",
      filterTanggal: "",
      filterKategori: "all",
      filterSudut: "",

      // State
      memuat: false,
      memuatKontingen: false,
      mengunduhPdf: false,
    };
  },

  computed: {
    daftarTersaring() {
      let hasil = [...this.matches];

      if (this.filterArena) {
        hasil = hasil.filter((m) => m.arena_name === this.filterArena);
      }

      if (this.filterTanggal) {
        hasil = hasil.filter((m) => m.scheduled_date === this.filterTanggal);
      }

      if (this.filterKategori !== "all") {
        hasil = hasil.filter((m) => m.match_type === this.filterKategori);
      }

      if (this.filterSudut) {
        hasil = hasil.filter((m) => m.corner === this.filterSudut);
      }

      const q = this.pencarian.trim().toLowerCase();
      if (q) {
        hasil = hasil.filter((m) => {
          const atlet = (m.athlete_name || "").toLowerCase();
          const lawan = (m.opponent_name || "").toLowerCase();
          const lawanKont = (m.opponent_contingent || "").toLowerCase();
          const kelas = (m.class_full || "").toLowerCase();
          const arena = (m.arena_name || "").toLowerCase();
          const partai = String(m.party_number || "");
          return (
            atlet.includes(q) ||
            lawan.includes(q) ||
            lawanKont.includes(q) ||
            kelas.includes(q) ||
            arena.includes(q) ||
            partai.includes(q)
          );
        });
      }

      return hasil;
    },
  },

  async mounted() {
    await this.muatDaftarKontingen();
  },

  methods: {
    labelJK,
    async muatDaftarKontingen() {
      this.memuatKontingen = true;
      try {
        const res = await http.get("/contingent-schedules/contingents");
        this.contingents = res.data?.contingents || [];

        // Jika hanya ada 1 kontingen (umumnya Official), langsung pilih otomatis
        if (this.contingents.length > 0) {
          this.contingentId = this.contingents[0].id;
          await this.muatJadwal();
        }
      } catch (err) {
        console.error("Gagal memuat kontingen:", err);
        this.toast.error("Gagal memuat daftar kontingen.");
      } finally {
        this.memuatKontingen = false;
      }
    },

    async muatJadwal() {
      if (!this.contingentId) return;

      this.memuat = true;
      try {
        const res = await http.get(`/contingent-schedules/${this.contingentId}`);
        const data = res.data || {};

        this.dataKontingen = data.contingent || null;
        this.dataTurnamen = data.tournament || null;
        this.matches = data.matches || [];
        this.stats = data.stats || this.stats;
        this.arenas = data.arenas || [];
        this.dates = data.dates || [];
      } catch (err) {
        console.error("Gagal memuat jadwal kontingen:", err);
        this.toast.error("Gagal memuat jadwal pertandingan kontingen.");
      } finally {
        this.memuat = false;
      }
    },

    async unduhPdf() {
      if (!this.contingentId) return;

      this.mengunduhPdf = true;
      try {
        const res = await http.get(`/contingent-schedules/${this.contingentId}/export-pdf`, {
          responseType: "blob",
          params: {
            arena_name: this.filterArena || undefined,
            scheduled_date: this.filterTanggal || undefined,
            match_type: this.filterKategori !== "all" ? this.filterKategori : undefined,
            search: this.pencarian || undefined,
          },
        });

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const namaBersih = (this.dataKontingen?.name || "Kontingen").replace(/[^a-zA-Z0-9_-]/g, "_");
        link.setAttribute("download", `Jadwal_Pertandingan_${namaBersih}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.toast.success("Jadwal PDF berhasil diunduh.");
      } catch (err) {
        console.error("Gagal mengunduh PDF jadwal:", err);
        this.toast.error("Gagal mengunduh PDF jadwal pertandingan.");
      } finally {
        this.mengunduhPdf = false;
      }
    },

    resetFilter() {
      this.pencarian = "";
      this.filterArena = "";
      this.filterTanggal = "";
      this.filterKategori = "all";
      this.filterSudut = "";
    },

    formatTanggal(tglStr) {
      if (!tglStr) return "-";
      try {
        const d = new Date(tglStr);
        return d.toLocaleDateString("id-ID", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return tglStr;
      }
    },
  },
};
</script>

<style scoped>
.extra-small {
  font-size: 11px;
}

.filter-select {
  font-size: 11.5px;
}

.text-purple {
  color: #7c3aed;
}

.text-blue-custom {
  color: #2563eb;
}

.partai-num {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  display: block;
  line-height: 1;
}

.badge-corner {
  display: inline-block;
  padding: 3px 6px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 3px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #fff;
}

.badge-corner--sm {
  padding: 2px 5px;
  font-size: 9.5px;
}

.badge-corner--blue {
  background-color: #2563eb;
}

.badge-corner--red {
  background-color: #dc2626;
}

.badge-corner--neutral {
  background-color: #4b5563;
}

.next-pill {
  font-size: 11.5px;
  line-height: 1.25;
}

.next-pill--next {
  color: #065f46;
  font-weight: 600;
}

.next-pill--final {
  color: #92400e;
  font-weight: 700;
  background-color: #fef3c7;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.border-start-blue {
  border-left: 4px solid #2563eb !important;
}

.border-start-red {
  border-left: 4px solid #dc2626 !important;
}

.custom-schedule-table tbody tr:hover {
  background-color: #f8fafc;
}
</style>
